import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';

const API_BASE_URL = (() => {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1') return 'https://api.neogtm.com';
  return 'https://api.neogtm.com';
})();

export default function SettingsOutlookCallback() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState(t('completingOutlookConnection'));

  useEffect(() => {
    const run = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      if (error) {
        const errorMsg = errorDescription || error || t('authorizationFailedOrCancelled');
        setStatus('error');
        setMessage(errorMsg);
        localStorage.removeItem('outlook_oauth_tenant_id');
        localStorage.removeItem('outlook_oauth_user_id');

        try {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              { type: 'oauth:complete', provider: 'outlook', success: false, error: errorMsg },
              window.location.origin
            );
            window.close();
          } else {
            setTimeout(() => (window.location.href = '/Settings'), 2000);
          }
        } catch {
          setTimeout(() => (window.location.href = '/Settings'), 2000);
        }
        return;
      }

      if (!code) throw new Error('No authorization code received');

      const tenantId = localStorage.getItem('outlook_oauth_tenant_id');
      const userId = localStorage.getItem('outlook_oauth_user_id');

      if (!userId || !tenantId) {
        throw new Error('User or tenant information not found. Please try connecting again from Settings.');
      }

      const resp = await fetch(`${API_BASE_URL}/api/outlook/oauth/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          code, 
          tenant_id: tenantId, 
          user_id: userId,
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to exchange code (${resp.status})`);
      }

      const data = await resp.json();

      if (data?.error) {
        throw new Error(data.error);
      }

      localStorage.removeItem('outlook_oauth_tenant_id');
      localStorage.removeItem('outlook_oauth_user_id');
      localStorage.setItem('outlook_connected', 'true');
      localStorage.setItem('outlook_email', data.user_email || '');

      setStatus('success');
      setMessage(t('outlookConnectedClosingWindow'));

      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: 'oauth:complete',
              provider: 'outlook',
              success: true,
              email: data.user_email,
              displayName: data.display_name,
            },
            window.location.origin
          );
          window.close();
        } else {
          setTimeout(() => (window.location.href = '/Settings'), 500);
        }
      } catch {
        setTimeout(() => (window.location.href = '/Settings'), 500);
      }
    } catch (e) {
      console.error('SettingsOutlookCallback Error:', e);
      setStatus('error');
      setMessage(e?.message || t('failedToConnectOutlook'));
      localStorage.removeItem('outlook_oauth_tenant_id');
      localStorage.removeItem('outlook_oauth_user_id');

      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            { type: 'oauth:complete', provider: 'outlook', success: false, error: e?.message || 'Unknown error' },
            window.location.origin
          );
          window.close();
        } else {
          setTimeout(() => (window.location.href = '/Settings'), 2500);
        }
      } catch {
        setTimeout(() => (window.location.href = '/Settings'), 2500);
      }
    }
    };
    run();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex flex-col items-center text-center space-y-4">
          {status === 'processing' && (
            <>
              <Loader2 className="w-12 h-12 text-[#0078D4] animate-spin" />
              <h2 className="text-xl font-semibold text-gray-900">{message}</h2>
              <p className="text-sm text-gray-500">{t('pleaseWait')}</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">{message}</h2>
              <p className="text-sm text-gray-500">{t('windowWillCloseAutomatically')}</p>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-red-600" />
              <h2 className="text-xl font-semibold text-gray-900">{t('connectionFailed')}</h2>
              <p className="text-sm text-gray-600">{message}</p>
              <p className="text-sm text-gray-500 mt-2">{t('youCanCloseThisWindow')}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}